import type { EntityType } from 'consts/flows';
import { DEFAULT_MIFID_STATUS } from 'consts/statuses';
import {
	appParams,
	decorateUserRelations,
	findSelected,
	getDefaultRelations,
	getMifidStatus,
	getNextPage,
	getPageForStatus,
	getRelationsURL,
	logger
} from 'helpers';
import { ApplicationContext } from 'providers/ApplicationContext';
import { createContext, useContext, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router';
import { baseAction, setDataAction } from 'store/actions';
import type { EntityProcessInfo, UserRelations } from 'types';

export type AppCtx = ReturnType<typeof BuildAppContext>;

export const AppContext = createContext({} as AppCtx);

export function BuildAppContext() {
	const ctx = useContext(ApplicationContext);
	const { entityType, flowType } = appParams(useParams());
	const { config, http, state, dataSelector, dispatch } = ctx;
	const { API, IS_EMPLOYEE_WEB } = config;
	const { context, user } = state;
	const { kennitala } = user;
	const { processInfo } = context;
	const { mifidStatus } = dataSelector(`${API.getMifidStatus}?ssn=${kennitala}`, DEFAULT_MIFID_STATUS);
	const relationsURL = getRelationsURL(config, state.user, flowType);
	const navigate = useNavigate();
	const goBack = () => navigate(-1);

	const goToNextPage = () => {
		const page = getNextPage(processInfo?.processStatus, flowType, entityType);
		page && navigate(`../${page}`);
	};

	const getRelations = (): UserRelations => {
		return dataSelector(relationsURL, getDefaultRelations());
	};

	const fetchRelations = async (redirect = false, newEntityType?: EntityType) => {
		try {
			const [relations, status] = (await Promise.all([
				http.get(relationsURL),
				getMifidStatus(kennitala, flowType, API, http)
			])) as [UserRelations, { mifidStatus: number }];

			const data = decorateUserRelations(relations, flowType, status.mifidStatus, IS_EMPLOYEE_WEB);
			const selected = findSelected(data, context.kennitala);
			dispatch([setDataAction, { data: { [relationsURL]: data } }]);

			if (selected) {
				dispatch([baseAction, { context: selected }]);
				if (redirect) {
					const entity = newEntityType ?? entityType;
					const page = getPageForStatus(selected.processInfo?.processStatus, flowType, entity);
					navigate(`../../../${flowType}/${entity}/${page}`);
				}
			}

			return data;
		} catch (error) {
			logger('Unable to fetch relations:', error);
			return getRelations();
		}
	};

	useEffect(() => {
		if (flowType !== state.user.flowType) {
			dispatch([
				baseAction,
				{
					context: {} as EntityProcessInfo,
					data: {},
					riskq: [],
					user: { ...state.user, entityType, flowType }
				}
			]);
			navigate(`/${flowType}/${entityType}/onboard`);
		}
	}, [flowType]); // eslint-disable-line react-hooks/exhaustive-deps

	return {
		...ctx,
		entityType,
		fetchRelations,
		flowType,
		getRelations,
		goBack,
		goToNextPage,
		mifidStatus,
		navigate
	};
}
